import type { ReactElement } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { useExtensionUsers } from '@/hooks/api/useExtensions';
import { IDEBadge } from '@/components/shared/extensions';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from './InventoryUserListDialog';

interface ExtensionUserListModalProps {
  organizationId: string;
  extensionId: string | null;
  extensionDisplayName?: string;
  onClose: () => void;
}

/**
 * Modal dialog showing list of users who have a specific extension installed.
 * Displays user details including sources (IDEs), version, and last seen time.
 */
export function ExtensionUserListModal({
  organizationId,
  extensionId,
  extensionDisplayName,
  onClose,
}: ExtensionUserListModalProps): ReactElement {
  const { data, isLoading, error } = useExtensionUsers(
    organizationId,
    extensionId || ''
  );
  const users = data?.users ?? [];

  return (
    <InventoryUserListDialog
      open={extensionId !== null}
      onClose={onClose}
      title={
        <>
          Users with{' '}
          <span className="font-mono">
            {extensionDisplayName || extensionId}
          </span>
        </>
      }
      columns={[
        { label: 'User' },
        { label: 'IDEs' },
        { label: 'Version' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={users.length === 0}
      emptyMessage="No users found with this extension"
    >
      {users.map(user => (
        <TableRow key={user.userId}>
          <TableCell>
            <div>
              <div className="font-medium">{user.name || 'Unknown User'}</div>
              {user.email && (
                <div className="text-sm text-muted-foreground">
                  {user.email}
                </div>
              )}
            </div>
          </TableCell>
          <TableCell>
            <IDEBadge sources={user.sources} />
          </TableCell>
          <TableCell className="font-mono text-sm">{user.version}</TableCell>
          <TableCell className="text-muted-foreground">
            <RelativeTimeWithTooltip date={user.lastSeenAt} showIcon={false} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
